// 数据同步 / aibijia 镜像表预览屏。
//
// aibijia.org 是聚合站 —— 它已经替我们从 LDXP / RedeemGPT / Auto Subscribe
// / beibei 4 家把 ChatGPT / Gemini / Claude Code / Grok 的价格 + 库存状态
// 都拉好了。我们消费它的 products.json 写入 D1，本屏只读展示。
//
// 跟 zzshu 不同的几点：
//   - aibijia offer 没有具体库存数（只有 in_stock / out_of_stock 二元）
//   - 没有详情 HTML 或主图
//   - 因此**不做"导入到 fuelio products"流水** —— 这种粗粒度比价数据
//     直接落到 fuelio.products 里信息不够。想要详情请去对应上游 URL 看。

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, ChevronLeft, ExternalLink, X, Download, Unlink2, FileSearch } from 'lucide-react'
import {
  api, errorMessage,
  type AibijiaProductRow, type AibijiaOfferRow, type AibijiaOfferFullRow,
} from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageError, PageLoading } from './Stats'
import { DataSyncAibijiaImportModal } from './DataSyncAibijiaImportModal'

function fmtRelative(unix: number | null): string {
  if (!unix) return '—'
  const diff = Math.floor(Date.now() / 1000) - unix
  if (diff < 60) return `${diff} 秒前`
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`
  return `${Math.floor(diff / 86400)} 天前`
}

function fmtPrice(cents: number | null, currency: string | null): string {
  if (cents === null) return '—'
  const sym = currency === 'USD' ? '$' : currency === 'CNY' ? '¥' : (currency ?? '') + ' '
  const v = (cents / 100).toFixed(2).replace(/\.00$/, '')
  return `${sym}${v}`
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>
  // 上游实际给三档（实测 D1 distinct 结果）：in_stock / low_stock / out_of_stock
  const styles: Record<string, string> = {
    in_stock:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    low_stock:    'bg-amber-100   text-amber-700   dark:bg-amber-900/40   dark:text-amber-300',
    out_of_stock: 'bg-red-100     text-red-700     dark:bg-red-900/40     dark:text-red-300',
  }
  const labels: Record<string, string> = {
    in_stock:     '有货',
    low_stock:    '少量',
    out_of_stock: '缺货',
  }
  const cls = styles[status] ?? 'bg-muted text-muted-foreground'
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {labels[status] ?? status}
    </span>
  )
}

const PRODUCT_LABEL: Record<string, string> = {
  'chatgpt':     'ChatGPT',
  'gemini':      'Gemini',
  'claude-code': 'Claude Code',
  'grok':        'Grok',
}

export function DataSyncAibijiaScreen() {
  const toast = useToast()
  const [products, setProducts] = useState<AibijiaProductRow[]>([])
  const [offers, setOffers] = useState<AibijiaOfferRow[]>([])
  const [platforms, setPlatforms] = useState<string[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [productFilter, setProductFilter] = useState<string | null>(null)
  const [platformFilter, setPlatformFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'in_stock' | 'low_stock' | 'out_of_stock' | null>(null)

  // 查看 / 导入 modal + 操作中状态
  const [detailId, setDetailId] = useState<string | null>(null)
  const [detail, setDetail] = useState<AibijiaOfferFullRow | null>(null)
  const [detailErr, setDetailErr] = useState<string | null>(null)
  const [importId, setImportId] = useState<string | null>(null)
  const [resyncing, setResyncing] = useState<Set<string>>(new Set())
  const [unlinkConfirm, setUnlinkConfirm] = useState<{ offerId: string; sku: string } | null>(null)
  // "重新抓取 LDXP 详情" 批量操作
  const [enriching, setEnriching] = useState(false)

  const load = async () => {
    setLoading(true); setErr(null)
    try {
      const [p, o] = await Promise.all([
        api.dataSyncAibijiaProducts(),
        api.dataSyncAibijiaOffers({
          ...(productFilter ? { product: productFilter } : {}),
          ...(platformFilter ? { platform: platformFilter } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
        }),
      ])
      setProducts(p.products)
      setOffers(o.offers)
      setPlatforms(o.platforms)
    } catch (e) {
      setErr(errorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [productFilter, platformFilter, statusFilter])

  const productBySlug = useMemo(() => {
    const m = new Map<string, AibijiaProductRow>()
    for (const p of products) m.set(p.slug, p)
    return m
  }, [products])

  const openDetail = async (id: string) => {
    setDetailId(id); setDetail(null); setDetailErr(null)
    try {
      const r = await api.dataSyncAibijiaOffer(id)
      setDetail(r.offer)
    } catch (e) {
      setDetailErr(errorMessage(e))
    }
  }
  const closeDetail = () => { setDetailId(null); setDetail(null); setDetailErr(null) }

  async function resync(offerId: string) {
    setResyncing((prev) => new Set(prev).add(offerId))
    try {
      const r = await api.dataSyncAibijiaResync(offerId)
      const bits: string[] = []
      if (r.syncedPrice !== null) bits.push(`价格 ¥${(r.syncedPrice / 100).toFixed(2)}`)
      if (r.syncedStock !== null) bits.push(`库存 ${r.syncedStock}`)
      toast.success(`${r.sku} 已更新${bits.length ? '：' + bits.join(' / ') : ''}`)
      await load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setResyncing((prev) => { const n = new Set(prev); n.delete(offerId); return n })
    }
  }

  // 批量"重新抓取所有上游详情" —— 顺序调每个支持的平台的 enrich endpoint。
  // 当前支持：LDXP (goodsInfo JSON API) + beibei (Next.js HTML parse)。
  // Auto Subscribe / RedeemGPT 还没写抓取器，自然不会被处理（这两家 offer 还
  // 是显示"未富化"）。各家 ~20 秒，合计 1 分钟。
  async function enrichAll() {
    setEnriching(true)
    try {
      // 并发跑：两家独立 endpoint 互不影响，等都回来再合并 toast
      const [ldxpRes, beibeiRes] = await Promise.allSettled([
        api.dataSyncAibijiaEnrichLdxp(),
        api.dataSyncAibijiaEnrichBeibei(),
      ])
      const bits: string[] = []
      if (ldxpRes.status === 'fulfilled') {
        const r = ldxpRes.value
        if (r.attempted > 0) bits.push(`LDXP ${r.succeeded}/${r.attempted}${r.failed ? `（失败 ${r.failed}）` : ''}`)
      } else {
        bits.push(`LDXP 异常：${errorMessage(ldxpRes.reason)}`)
      }
      if (beibeiRes.status === 'fulfilled') {
        const r = beibeiRes.value
        if (r.attempted > 0) bits.push(`beibei ${r.succeeded}/${r.attempted}${r.failed ? `（失败 ${r.failed}）` : ''}`)
      } else {
        bits.push(`beibei 异常：${errorMessage(beibeiRes.reason)}`)
      }
      const ldxpSummary    = ldxpRes.status === 'fulfilled' ? ldxpRes.value : null
      const beibeiSummary  = beibeiRes.status === 'fulfilled' ? beibeiRes.value : null
      // 把"全军覆没"型的失败优先用 error toast 提示，附 hint。普通部分失败/全成功
      // 用 success toast 汇总。
      if (ldxpSummary?.allWafBlocked) {
        toast.error('LDXP 全部被 WAF 拦截', ldxpSummary.hint ?? '')
      } else if (beibeiSummary?.allNetworkLost) {
        toast.error('beibei 全部网络不通（地理屏蔽）', beibeiSummary.hint ?? '')
      } else if (bits.length === 0) {
        toast.info('没有可抓取的 offers，先跑一次 aibijia 导入')
      } else {
        toast.success('抓取完成', bits.join('  ·  '))
      }
      await load()
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setEnriching(false)
    }
  }

  async function unlink() {
    if (!unlinkConfirm) return
    try {
      await api.dataSyncAibijiaUnlink(unlinkConfirm.offerId)
      toast.success(`已解除链接 ${unlinkConfirm.sku}`)
      setUnlinkConfirm(null)
      await load()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  if (loading && offers.length === 0) return <PageLoading/>
  if (err && offers.length === 0) return <PageError msg={err}/>

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/data-sync" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4"/>返回数据同步
        </Link>
        <div className="flex items-center gap-2">
          {/* 重新抓取详情 —— 并发触发各平台的 enrich endpoint。当前 LDXP（API）+
              beibei（HTML 解析）已经接入；Auto Subscribe / RedeemGPT 后续加。 */}
          <Button
            variant="outline" size="sm"
            onClick={() => void enrichAll()}
            disabled={enriching}
            title="对每个支持的平台并发抓取上游详情（LDXP + beibei），结果写入 aibijia_offer_enrichments"
          >
            <FileSearch className={`w-4 h-4 mr-2 ${enriching ? 'animate-pulse' : ''}`}/>
            {enriching ? '抓取中…' : '重新抓取详情'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="w-4 h-4 mr-2"/>刷新
          </Button>
        </div>
      </header>

      <div>
        <h1 className="text-2xl font-semibold">AI 比价（aibijia.org）</h1>
        <p className="text-sm text-muted-foreground mt-1">
          聚合 4 家平台的 ChatGPT / Gemini / Claude Code / Grok 价格 + 库存状态。共
          <span className="font-mono"> {offers.length} </span>条 offer。
          重跑导入：<code className="text-xs bg-muted px-1.5 py-0.5 rounded">cd crawlers/aibijia && pnpm import:local</code>
        </p>
      </div>

      {/* 4 个产品概览（点击切换筛选） */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {products.map((p) => {
          const active = productFilter === p.slug
          return (
            <button
              key={p.slug}
              onClick={() => setProductFilter(active ? null : p.slug)}
              className={`p-3 rounded-lg border text-left transition ${
                active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
              }`}
            >
              <div className="text-xs text-muted-foreground font-mono mb-1">{p.slug}</div>
              <div className="font-medium">{PRODUCT_LABEL[p.slug] ?? p.name}</div>
              <div className="text-xs text-muted-foreground mt-1">
                <span className="font-mono">{p.offers_count}</span> offers
              </div>
            </button>
          )
        })}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">平台:</span>
            <button
              className={`px-3 py-1 rounded text-xs ${platformFilter === null ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
              onClick={() => setPlatformFilter(null)}
            >全部</button>
            {platforms.map((pn) => (
              <button
                key={pn}
                className={`px-3 py-1 rounded text-xs ${platformFilter === pn ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                onClick={() => setPlatformFilter(pn)}
              >{pn}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">库存:</span>
            {([
              [null,           '全部'],
              ['in_stock',     '有货'],
              ['low_stock',    '少量'],
              ['out_of_stock', '缺货'],
            ] as const).map(([v, label]) => (
              <button
                key={String(v)}
                className={`px-3 py-1 rounded text-xs ${statusFilter === v ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                onClick={() => setStatusFilter(v as any)}
              >{label}</button>
            ))}
          </div>
        </div>
      </Card>

      {/* Offers table */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th className="w-24">产品</Th>
              <Th className="w-32">平台</Th>
              <Th>商品标题</Th>
              <Th className="w-24 text-right">价格</Th>
              <Th className="w-16">库存</Th>
              <Th className="w-32">fuelio 链接</Th>
              <Th className="w-20 text-right">同步</Th>
              <Th className="w-60 text-right">操作</Th>
            </Tr>
          </THead>
          <TBody>
            {offers.map((o) => {
              const product = productBySlug.get(o.product_slug)
              const linked = !!o.fuelio_sku
              return (
                <Tr key={o.id} className="hover:bg-muted/40">
                  <Td>
                    <Badge>{PRODUCT_LABEL[o.product_slug] ?? product?.name ?? o.product_slug}</Badge>
                  </Td>
                  <Td>
                    <div className="text-sm flex items-center gap-1.5">
                      {o.platform_name ?? '—'}
                      {/* 富化标记：enrich-ldxp 等脚本写入过详情时，UI 角标示意
                          导入 modal 能自动预填 markdown + 主图。 */}
                      {o.enriched_at && (
                        <span
                          title={`已抓取详情 ${fmtRelative(o.enriched_at)}`}
                          className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"
                        />
                      )}
                    </div>
                    {o.source_store_name && o.source_store_name !== o.platform_name && (
                      <div className="text-xs text-muted-foreground mt-0.5">{o.source_store_name}</div>
                    )}
                  </Td>
                  <Td>
                    <div className="text-sm leading-snug line-clamp-2 max-w-xl">{o.source_title ?? '—'}</div>
                  </Td>
                  <Td className="text-right font-mono">{fmtPrice(o.price_cents, o.currency)}</Td>
                  <Td><StatusBadge status={o.status}/></Td>
                  <Td>
                    {linked ? (
                      <div className="text-xs">
                        <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-mono">
                          ✓ {o.fuelio_sku}
                        </span>
                        {o.link_synced_at && (
                          <div className="text-muted-foreground mt-1">{fmtRelative(o.link_synced_at)} 已更新</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">未导入</span>
                    )}
                  </Td>
                  <Td className="text-right text-xs text-muted-foreground">{fmtRelative(o.synced_at)}</Td>
                  <Td className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => void openDetail(o.id)}>查看</Button>
                    {linked ? (
                      <>
                        <Button
                          size="sm" variant="outline"
                          disabled={resyncing.has(o.id)}
                          onClick={() => void resync(o.id)}
                          title="把上游最新 价格/库存(估算) 同步给已链接的 fuelio product"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${resyncing.has(o.id) ? 'animate-spin' : ''}`}/>
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setUnlinkConfirm({ offerId: o.id, sku: o.fuelio_sku! })}
                          title="解除映射（不删 fuelio product）"
                        >
                          <Unlink2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => setImportId(o.id)}>
                        <Download className="w-3.5 h-3.5 mr-1"/>导入
                      </Button>
                    )}
                  </Td>
                </Tr>
              )
            })}
          </TBody>
        </Table>
        {offers.length === 0 && !loading && (
          <div className="p-8 text-center text-muted-foreground text-sm">
            没有符合条件的 offer。先跑一次导入：<code>cd crawlers/aibijia && pnpm import:local</code>
          </div>
        )}
      </Card>

      {/* 导入 modal */}
      {importId !== null && (
        <DataSyncAibijiaImportModal
          offerId={importId}
          onClose={() => setImportId(null)}
          onSuccess={() => { setImportId(null); void load() }}
        />
      )}

      {/* 解链确认 */}
      <ConfirmDialog
        open={!!unlinkConfirm}
        title="解除链接"
        message={unlinkConfirm ? `确定解除 ${unlinkConfirm.sku} 与 aibijia 上游的映射？fuelio 主库的商品不会被删，但以后"更新"按钮就找不到对应上游了。` : ''}
        confirmLabel="解除"
        cancelLabel="取消"
        tone="danger"
        onConfirm={() => void unlink()}
        onCancel={() => setUnlinkConfirm(null)}
      />

      {/* 查看 modal —— 没富化时只有 aibijia 摘要；富化后展开富化数据 */}
      {detailId !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeDetail}
        >
          <div className="bg-card border border-border rounded-lg max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <h3 className="font-semibold text-sm">Offer 详情 · {detail?.platform_name ?? '...'}</h3>
              <button onClick={closeDetail} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {detailErr && <PageError msg={detailErr}/>}
              {!detail && !detailErr && <PageLoading/>}
              {detail && (
                <>
                  {/* 标题 + 富化时显示主图 */}
                  <div className="flex items-start gap-4">
                    {detail.enriched_cover_url && (
                      <a href={detail.enriched_cover_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                        <img src={detail.enriched_cover_url} alt="" className="w-32 h-32 object-cover rounded border border-border"/>
                      </a>
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-medium leading-snug">
                        {detail.enriched_name?.trim() || detail.source_title || '—'}
                      </div>
                      {detail.enriched_name && detail.source_title && detail.enriched_name !== detail.source_title && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">aibijia: {detail.source_title}</div>
                      )}
                    </div>
                  </div>

                  {/* 元信息网格 */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    <div><span className="text-muted-foreground">产品大类</span> <span>{PRODUCT_LABEL[detail.product_slug] ?? detail.product_slug}</span></div>
                    <div><span className="text-muted-foreground">平台</span> <span>{detail.platform_name ?? '—'}</span></div>
                    <div><span className="text-muted-foreground">店家</span> <span>{detail.source_store_name ?? '—'}</span></div>
                    <div>
                      <span className="text-muted-foreground">价格</span>{' '}
                      <span className="font-mono">{fmtPrice(detail.enriched_price_cents ?? detail.price_cents, detail.currency)}</span>
                      {detail.enriched_price_cents && detail.enriched_price_cents !== detail.price_cents && detail.price_cents && (
                        <span className="text-[10px] text-muted-foreground ml-1">(aibijia: {fmtPrice(detail.price_cents, detail.currency)})</span>
                      )}
                    </div>
                    <div><span className="text-muted-foreground">库存</span> <StatusBadge status={detail.status}/></div>
                    <div><span className="text-muted-foreground">上次同步</span> <span>{fmtRelative(detail.synced_at)}</span></div>
                    {detail.fuelio_sku && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">已链接到 fuelio </span>
                        <span className="font-mono text-emerald-700 dark:text-emerald-300">{detail.fuelio_sku}</span>
                      </div>
                    )}
                  </div>

                  {/* 标签 */}
                  {detail.display_tags && detail.display_tags !== '[]' && (
                    <div className="text-xs">
                      <span className="text-muted-foreground">标签：</span>
                      <span className="font-mono">{detail.display_tags}</span>
                    </div>
                  )}

                  {/* 富化的 markdown 描述 */}
                  {detail.enriched_description_md ? (
                    <div className="border-t border-border pt-3">
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-xs font-medium">宝贝详情（已抓取 · {detail.enriched_description_md.length} 字）</span>
                        <span className="text-[10px] text-muted-foreground">
                          来源 <span className="font-mono uppercase">{detail.enriched_source ?? 'upstream'}</span>
                          {detail.enriched_fetched_at && ` · ${new Date(detail.enriched_fetched_at * 1000).toLocaleString('zh-CN')}`}
                        </span>
                      </div>
                      <pre className="bg-muted/40 border border-border rounded p-3 text-xs leading-relaxed whitespace-pre-wrap font-mono overflow-x-auto max-h-[300px]">
                        {detail.enriched_description_md}
                      </pre>
                    </div>
                  ) : (
                    <div className="border-t border-border pt-3 text-xs text-muted-foreground italic">
                      该 offer 还未"富化"，详情数据 aibijia 不提供。点击下方"打开上游"看实际商品页，或用顶部"重新抓取 LDXP 详情"按钮（仅 LDXP）。
                    </div>
                  )}

                  {/* 底部链接 */}
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    {detail.url ? (
                      <a
                        href={detail.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        打开上游商品页 <ExternalLink className="w-3.5 h-3.5"/>
                      </a>
                    ) : <span/>}
                    <span className="text-[10px] text-muted-foreground">链接外键：<code className="font-mono">{detail.url ?? '—'}</code></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
